import {
  Alert,
  AlertTitle,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from "@mui/material";
import { FormBlock, Toolbar, globalMessageHandler } from "common";
import CheckboxButton from "common/components/Inputs/CheckboxButton/CheckboxButton";
import CounterButton from "common/components/Inputs/CounterButton/CounterButton";
import Price from "common/components/Inputs/Price/Price";
import { BaseInputProps } from "common/components/Inputs/types";
import { FakeEvent } from "common/types";
import { BookingOptionItem } from "features/booking-options/bookingOptionApi";
import {
  BookingItem,
  BookingOptionGroupItem,
  ExtendedEventOption,
} from "features/bookings/bookingApi";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { useContext } from "react";
import { BookingContext } from "./BookingForm";

export interface BookingOptionSelectorProps extends BaseInputProps<
  BookingOptionItem[]
> {
  options?: BookingOptionGroupItem[];
  booking: BookingItem;
  onChange?: (event: FakeEvent<BookingOptionItem[]>) => void;
}
export default function BookingOptionSelector({
  value,
  onChange,
  options,
  booking,
  disabled,
}: BookingOptionSelectorProps) {
  console.log({ disabled });
  const bookingForm = useContext(BookingContext);
  const { hasPermissions } = useAccess();
  const number_of_guests =
    (booking.number_of_adults || 0) + (booking.number_of_kids || 0);

  function getCurrentQty(optionId: number) {
    return value?.find(v => v.program_option_id === optionId)?.qty || 0;
  }

  function getLimitInfo(item: ExtendedEventOption) {
    if (item.limit === null || item.limit === undefined) {
      return null;
    }
    const limit = Number(item.limit);
    if (!Number.isFinite(limit) || limit <= 0) {
      return null;
    }

    const soldQty = Math.max(0, Number(item.number_of_bookings || 0));
    const availableQty = Math.max(0, limit - soldQty);

    return {
      limit,
      soldQty,
      availableQty,
    };
  }

  function getMaxQty(item: ExtendedEventOption): number | null {
    const limitInfo = getLimitInfo(item);
    return limitInfo ? limitInfo.availableQty : null;
  }

  function clampQtyByLimit(newCount: number, item: ExtendedEventOption) {
    const maxQty = getMaxQty(item);
    if (maxQty === null) {
      return newCount;
    }
    if (newCount <= maxQty) {
      return newCount;
    }

    const limitInfo = getLimitInfo(item);
    const message = limitInfo
      ? `A(z) ${item.name} opció elérhető mennyisége elérve. Limit: ${limitInfo.limit}, foglalt: ${limitInfo.soldQty}, még adható: ${limitInfo.availableQty}.`
      : `A(z) ${item.name} opció elérhető mennyisége elérve. Maximum ${maxQty} adható.`;

    globalMessageHandler.alert({
      message: (
        <Typography sx={{ color: "#7b1fa2" }} fontWeight={600}>
          {message}
        </Typography>
      ) as any,
    });
    return maxQty;
  }

  function handleChangeItem(evt: any, item: ExtendedEventOption) {
    const requestedCount = evt.target?.value || 0;
    const newCount = clampQtyByLimit(requestedCount, item);
    let newValue = value ? value.map(v => Object.assign({}, v)) : null;
    if (!newValue) {
      newValue = [];
    }

    const index = newValue.findIndex(v => v.program_option_id === item.id);

    if (index === -1) {
      newValue.push({
        //...item,
        //id:-1,
        booking_id: booking.id,
        program_option_id: item.id,
        option: item,
        qty: newCount,
        price: item.price ? parseFloat(item.price as any) : 0,
        //price: getOptionPrices(item.id),
      } as any);
    } else {
      newValue[index].qty = newCount;
    }
    onChange && onChange({ target: { value: newValue } });
  }
  function handleChangeItemPrice(evt: any, item: ExtendedEventOption) {
    const newPrice = evt.target?.value || 0;
    let newValue = value ? value.map(v => Object.assign({}, v)) : null;
    if (!newValue) {
      newValue = [];
    }

    const index = newValue.findIndex(v => v.program_option_id === item.id);

    if (index >= 0) {
      newValue[index].price = newPrice;
      onChange && onChange({ target: { value: newValue } });
    }
  }

  return (
    <FormBlock
    /*sx={{
        p: 2,
        border: theme => `solid 1px ${theme.palette.divider}`,
        backgroundColor: "unset",
        borderRadius: theme => `${theme.shape.borderRadius}px`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}*/
    >
      <Typography variant="h5">Opciók</Typography>
      {options?.map(group => {
        const hasOnlyOneOption = group.items.length === 1;
        let groupStatus = bookingForm?.optionValidator.validateGroup(group);
        let groupOptionCount =
          bookingForm?.optionValidator.getGroupOptionCount(group);
        let groupName = hasOnlyOneOption ? group.items[0].name : group?.name;
        let groupTitle = `${groupName} ${groupOptionCount}/${number_of_guests}`;

        return (
          <List key={group.id} disablePadding>
            <ListItem>
              {groupStatus === "less" && (
                <Alert severity="warning" sx={{ flex: 1 }}>
                  <AlertTitle>{groupTitle}</AlertTitle>
                  {"Nincs mindenkinek kiválasztva."}
                </Alert>
              )}
              {groupStatus === "more" && (
                <Alert severity="error" sx={{ flex: 1 }}>
                  <AlertTitle>{groupTitle}</AlertTitle>
                  {`Több ${groupName} van választva, mint ahány vendég van.`}
                </Alert>
              )}
              {groupStatus === "ok" && number_of_guests > 0 && (
                <Alert severity="success" sx={{ flex: 1 }}>
                  <AlertTitle>{groupTitle}</AlertTitle>
                  {`Mindenkinek jut ${groupName} :).`}
                </Alert>
              )}
              {groupStatus === "ok" && number_of_guests === 0 && (
                <Alert severity="info" sx={{ flex: 1 }}>
                  <AlertTitle>{groupTitle}</AlertTitle>
                  {`Először vegyél fel személyeket.`}
                </Alert>
              )}
              {groupStatus === null &&
                number_of_guests === 0 &&
                group.type === "guest" && (
                  <Alert severity="info" sx={{ flex: 1 }}>
                    <AlertTitle>{groupTitle}</AlertTitle>
                    {`Először vegyél fel személyeket.`}
                  </Alert>
                )}
              {!hasOnlyOneOption && !groupStatus && (
                <Typography>{groupName}</Typography>
              )}
            </ListItem>
            {group?.items?.map(option => {
              const v = value?.find(v => v.program_option_id === option.id);
              const qty = v?.qty || 0;
              const limitInfo = getLimitInfo(option);
              const maxQty = limitInfo ? limitInfo.availableQty : null;
              const atLimit = maxQty !== null && qty >= maxQty;
              const isClosed = !!option.closed;
              const limitText = isClosed
                ? I18n.t("Price.attributes.closed")
                : limitInfo
                  ? `Elérhető: ${limitInfo.availableQty}${atLimit ? " (elérve)" : ""}`
                  : undefined;
              const disableEdit =
                disabled ||
                isClosed ||
                bookingForm?.optionValidator.number_of_guests === 0 ||
                !hasPermissions([
                  "booking_option.editany",
                  "booking_option.editown",
                ]);
              //!hasPermissions("booking.editprice");
              return (
                <ListItem key={option.id} sx={{ gap: 2 }} divider>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        backgroundColor: theme =>
                          qty > 0
                            ? theme.palette.primary.main
                            : theme.palette.action.disabled,
                        color: theme =>
                          qty > 0
                            ? theme.palette.getContrastText(
                                theme.palette.primary.main,
                              )
                            : theme.palette.getContrastText(
                                theme.palette.action.disabled,
                              ),
                      }}
                    >
                      {qty}
                    </Avatar>
                  </ListItemAvatar>
                  <Toolbar sx={{ flexWrap: "wrap", flex: 1 }}>
                    <ListItemText
                      primary={option?.name}
                      secondary={limitText}
                      secondaryTypographyProps={{
                        component: "div",
                        color:
                          isClosed || atLimit ? "error.main" : "success.main",
                        noWrap: true,
                      }}
                    />
                  </Toolbar>
                  {!!group.has_price && (
                    <Price
                      disabled={
                        disableEdit ||
                        !v?.qty ||
                        !hasPermissions("booking.editprice")
                      }
                      useDelayedKeystrokes={false}
                      placeholder={
                        (option as any)?.price
                          ? String((option as any)?.price)
                          : undefined
                      }
                      //size="small"
                      value={v?.price}
                      onChange={evt => handleChangeItemPrice(evt, option)}
                      sx={{ maxWidth: 120 }}
                    />
                  )}
                  {group.type === "guest" && (
                    <CounterButton
                      value={v?.qty}
                      onChange={evt => handleChangeItem(evt, option)}
                      //size="small"
                      disabled={disableEdit}
                    />
                  )}
                  {group.type === "booking" && (
                    <CheckboxButton
                      value={v?.qty}
                      numeric
                      onChange={evt => {
                        if (!!evt?.target.value) {
                          handleChangeItem(
                            { target: { value: number_of_guests } },
                            option,
                          );
                        } else {
                          handleChangeItem({ target: { value: 0 } }, option);
                        }
                      }}
                      disabled={disableEdit}
                    />
                  )}
                </ListItem>
              );
            })}
          </List>
        );
      })}
    </FormBlock>
  );
}
